import { useEffect, useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import { shouldSyncQuery } from './useSelectState.helpers';

interface QuerySyncParams {
  value: string | null;
  selectedValue: string;
  setSelectedValue: Dispatch<SetStateAction<string>>;
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  isFocused: boolean;
  searchable: boolean;
  getDisplayNameForValue: (optionValue: string) => string;
}

/**
 * Syncs the controlled external value into local query/selected state when it
 * diverges, resolving the display label for non-empty values.
 *
 * @param value The controlled external value (nullable).
 * @param selectedValue The current internally selected value.
 * @param getDisplayNameForValue Resolver from value to display label.
 * @param setQuery Setter for the query text.
 * @param setSelectedValue Setter for the selected value.
 */
function syncExternalValue(
  value: string | null, selectedValue: string,
  getDisplayNameForValue: (v: string) => string,
  setQuery: Dispatch<SetStateAction<string>>,
  setSelectedValue: Dispatch<SetStateAction<string>>,
): void {
  const effective = value ?? '';
  if (effective === selectedValue) return;
  setQuery(effective ? getDisplayNameForValue(effective) : '');
  setSelectedValue(effective);
}

/**
 * On blur of a searchable select, restores the query text to the selected
 * value's display label when it drifted.
 *
 * @param isFocused Whether the input is currently focused.
 * @param selectedValue The current selected value.
 * @param query The current query text.
 * @param displayNameRef Ref caching the last known display label.
 * @param getDisplayNameForValue Resolver from value to display label.
 * @param setQuery Setter for the query text.
 */
function syncSearchableBlur(
  isFocused: boolean, selectedValue: string, query: string,
  displayNameRef: React.MutableRefObject<string>,
  getDisplayNameForValue: (v: string) => string,
  setQuery: Dispatch<SetStateAction<string>>,
): void {
  const expected = displayNameRef.current || getDisplayNameForValue(selectedValue);
  if (!shouldSyncQuery(isFocused, selectedValue, query, expected)) return;
  setQuery(expected);
  displayNameRef.current = expected;
}

/**
 * Effect that, on the focus rising edge with a selected value and non-empty
 * query, ensures the query reflects the selected value's display label.
 *
 * @param isFocused Whether the input is currently focused.
 * @param selectedValue The current selected value.
 * @param query The current query text.
 * @param ensureDisplayNameInQuery Callback that writes the display label.
 */
function useFocusSyncEffect(
  isFocused: boolean, selectedValue: string, query: string,
  ensureDisplayNameInQuery: (v: string) => void,
) {
  const prevIsFocusedRef = useRef(false);
  useEffect(() => {
    if (isFocused && !prevIsFocusedRef.current && selectedValue && query !== '') {
      ensureDisplayNameInQuery(selectedValue);
    }
    prevIsFocusedRef.current = isFocused;
  }, [isFocused, selectedValue, query, ensureDisplayNameInQuery]);
}

/**
 * Keeps the select's query text and selected value in sync with the controlled
 * external value and focus/blur transitions.
 *
 * @param params Controlled value, local state setters, focus flag, and config.
 * @returns `{ displayNameRef, ensureDisplayNameInQuery }` sync helpers.
 */
export function useSelectQuerySync({
  value, selectedValue, setSelectedValue,
  query, setQuery, isFocused, searchable, getDisplayNameForValue,
}: QuerySyncParams) {
  const displayNameRef = useRef<string>('');

  useEffect(() => {
    displayNameRef.current = selectedValue ? getDisplayNameForValue(selectedValue) : '';
  }, [selectedValue, getDisplayNameForValue]);

  const ensureDisplayNameInQuery = useCallback(
    (optionValue: string) => {
      const displayName = getDisplayNameForValue(optionValue);
      setQuery(displayName);
      displayNameRef.current = displayName;
    },
    [getDisplayNameForValue, setQuery],
  );

  useEffect(() => {
    syncExternalValue(value, selectedValue, getDisplayNameForValue, setQuery, setSelectedValue);
  }, [value, selectedValue, getDisplayNameForValue, setQuery, setSelectedValue]);

  useFocusSyncEffect(isFocused, selectedValue, query, ensureDisplayNameInQuery);

  useEffect(() => {
    if (!searchable) return;
    syncSearchableBlur(isFocused, selectedValue, query, displayNameRef, getDisplayNameForValue, setQuery);
  }, [isFocused, selectedValue, query, getDisplayNameForValue, searchable, setQuery]);

  return { displayNameRef, ensureDisplayNameInQuery };
}
